import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, useLocation, useParams, matchPath } from 'react-router-dom'
import { useCurrentAgencyUsers, useAuth, useFormFactor } from 'hooks'
import {
  PageTitleRightActions,
  PageTitleSubtitle,
  SidePanelContent,
} from 'components'
import {
  EditUser,
  AddUser,
  LicenseCount,
  CheckPermissions,
  AddMultipleUsers,
} from 'components/users'
import {
  Table,
  Snackbar,
  ConfirmationModal,
  IconButton,
  SplitButton,
} from 'cs-kevlar-react'
import { ColumnDef, MenuClick } from 'cs-kevlar-react/lib/es/table/base'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { canViewAgencyPage, matchIdOrDefaultWithBulk } from 'utils'
import { CustomerUser } from '../../contexts/base'
import { AddCircle, Edit } from 'cs-kevlar-react/lib/es/icons'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { DevicesPage, DEVICE_USERS_QUERY_PARAM, DEVICE_USER_QUERY_PARAM } from './Devices'
import {
  FilterOptions,
  PaginationOptions,
} from 'cs-kevlar-react/lib/es/table/Table'
import { UserDisplayName } from 'components/UserDisplayName'
import clsx from 'clsx'
import { WebAdminUser } from 'api'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const CheckPermissionPath =
  '/customers/:customerAgencyId/users/:customerUserId/permissions'

type UsersProps = {
  customerAgencyId: string
  licensedAgencyId: string
  userId?: string
}

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'user',
  pluralAdjective: 'users',
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    permissionChecking: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: 750,
        width: 750,
      },
    },
    licenseCount: {
      marginRight: theme.spacing(4),
    },
    licenseCountPhone: {
      marginBottom: theme.spacing(10),
    },
    uppercase: {
      textTransform: 'uppercase',
    },
    iconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
  }),
)

type ValidMenuKey =
  | 'Edit'
  | 'NebulaLogs'
  | 'CheckPermissions'

const defaultUser: CustomerUser = {
  id: 'NEW',
  userId: 'NEW',
  name: '',
  fullName: '',
  externalPhoneList: [],
  phone: '',
  externalEmailList: [],
  email: '',
  customerAgencyId: '',
  licensedAgencyId: '',
  licensedAgencyName: '',
  capabilities: [],
  permissions: [],
  globalConfiguration: null,
  ownerAgencyExternalId: '',
  userAgencyGlobalConfiguration: null,
  isActive: true,
}

const bulkDefaultUser: CustomerUser = {
  ...defaultUser,
  id: 'BULK',
  userId: 'BULK'
}

type AssistedAgency = {
  customerAgencyId: string
  licensedAgencyId: string
}

const buildPageTitle = (user: WebAdminUser | null) => {
  if (!user?.licensedAgencyName) return 'Users'

  return `${user.licensedAgencyName} - Users`
}

export const buildNavigationUrl = (
  userId: string | null,
  agency: AssistedAgency | null,
) => {
  if (!agency) {
    if (userId === null) return `/users`

    return `/users/${userId}`
  }

  if (userId === null)
    return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/users`

  return `/assist/customer/${agency.customerAgencyId}/agency/${agency.licensedAgencyId}/users/${userId}`
}

const Users: React.FunctionComponent = () => {

  const params = useParams<UsersProps>()
  if(!params) return null

  const navigate = useNavigate()
  const location = useLocation()
  const { user: authUser } = useAuth()
  const classes = useStyles({})
  const formFactor = useFormFactor()

  usePageViewAudit(buildPageTitle(authUser), !!authUser)

  const currentLicensedAgencyId =
    params.licensedAgencyId ?? authUser?.licensedAgencyId

  const {
    agency,
    agencyUsersLookup,
    licensedUsers,
    licensedUserLookup,
    adminUsers,
    nonLicensedUsers,
    initialize,
    loadLicensedUsers,
    loadAdminUsers,
    addLicensedUser,
    addLicensedUsers,
    editLicensedUser,
    deleteLicensedUser,
  } = useCurrentAgencyUsers(currentLicensedAgencyId)

  const customerAgencyId =
    params.customerAgencyId ?? agency?.customerAgencyId

  const navigateToRoot = () => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(null, null))

      navigate(
      buildNavigationUrl(null, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const selectUserForEditing = (user: CustomerUser | null) => {
    if (authUser === null) return

    if (!authUser.customerAgencyId || !authUser.licensedAgencyId) {
      navigate(buildNavigationUrl(user ? user.id : null, null))
      return
    }

    navigate(
      buildNavigationUrl(user?.id ? user.id : null, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const addUserOptions = [ 'Add New User', 'Add Multiple New Users']

  const selectAddUserOption = (option: string) => {
    if (option === addUserOptions[0])
      selectUserForEditing(defaultUser)
    else
      selectUserForEditing(bulkDefaultUser)
  }

  const editingUser = matchIdOrDefaultWithBulk(
    licensedUserLookup,
    params.userId,
    defaultUser,
    bulkDefaultUser
  )

  const isEditing = editingUser !== null && editingUser.id !== 'NEW' && editingUser.id !== 'BULK'
  const isAdding = editingUser !== null && editingUser.id === 'NEW'
  const isBulkAdding = editingUser !== null && editingUser.id === 'BULK'

  const handleMenuClick = (key: string, row: CustomerUser) => {
    if (key === 'Edit') {
      selectUserForEditing(row)
      return
    }

    if (key === 'CheckPermissions') {
      navigate(
        `/customers/${row.customerAgencyId}/users/${row.id}/permissions`,
      )
      return
    }

    if (key === 'NebulaLogs' && row.customerAgencyId && row.licensedAgencyId) {
      const link = document.createElement('a')
      link.href = `api/diagnostics/logs/customer/${row.customerAgencyId}/nebula?licensedAgencyId=${row.licensedAgencyId}&customerUserId=${row.id}`
      link.target = '_blank'
      link.click()
      return
    }
  }

  const permissionCheckMatch = matchPath({
    path: CheckPermissionPath,
    caseSensitive: true,
  }, location.pathname)
  const isPerformingPermissionCheck = permissionCheckMatch !== null

  const onDelete = () => {
    if (editingUser === null) return

    deleteLicensedUser(editingUser.id, navigateToRoot)
  }

  const onSave = (user: CustomerUser) => {
    if (!customerAgencyId || !user.id) return

    editLicensedUser(user, navigateToRoot)
  }

  const onAdd = async (user: CustomerUser) => {
    if (!customerAgencyId || !user.id) return

    user.userId = user.id
    user.customerAgencyId = customerAgencyId
    user.licensedAgencyId = currentLicensedAgencyId

    await addLicensedUser(user, () => {
      setAddedUser(user)
      navigateToRoot()
    })
  }

  const onAddMultipleUsers = async (users: CustomerUser[]) => {
    if (!customerAgencyId) return

    var usersToAdd = users.map((user) => {
      return {
        ...user,
        userId: user.id,
        customerAgencyId: customerAgencyId,
        licensedAgencyId: currentLicensedAgencyId
      }
    })

    await addLicensedUsers(usersToAdd, (addedUsers: CustomerUser[]) => {

      if (addedUsers.length == 1)
        setAddedUser(addedUsers[0])
      else
        setAddedUsers(addedUsers)

      navigateToRoot()
    })
  }

  const navigateToDevices = (addedUser: CustomerUser) => {
    var navLink =
      DevicesPage.buildNavLink(authUser) +
      `/NEW?${DEVICE_USER_QUERY_PARAM}=${addedUser.id}`
      navigate(navLink)
  }

  const navigateToDevicesBulk = (addedUsers: CustomerUser[]) => {

    if (addedUsers.length == 1)
    {
      navigateToDevices(addedUsers[0])
    }
    else
    {
      var navLink =
        DevicesPage.buildNavLink(authUser) +
        `/BULK?${DEVICE_USERS_QUERY_PARAM}=${addedUsers.map((user) => { return (user.userId) } )}`
        navigate(navLink)
    }
  }

  const [isLoading, setIsLoading] = React.useState(false)
  const [addedUser, setAddedUser] = React.useState<CustomerUser | null>(null)
  const [addedUsers, setAddedUsers] = React.useState<CustomerUser[] | null>(null)

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      await initialize()
      await loadLicensedUsers()
      await loadAdminUsers() //Changes to licensed users can affect admin users so load them also
      setIsLoading(false)
    }

    loadData()
  }, [])

  const activeNonLicensedUsers = React.useMemo(
    () => nonLicensedUsers.filter((x) => x.isActive),
    [nonLicensedUsers],
  )

  const activeAdminUsers = React.useMemo(
    () => adminUsers.filter((x) => x.isActive),
    [adminUsers],
  )

  const availableLicenses =
    (agency ? agency.licenseCount ?? 0 : 0) - licensedUsers.length

  const canViewLogs = authUser?.isSupport ?? false
  const canCheckPermissions = authUser?.isSupport ?? false

  const columns: ColumnDef<CustomerUser, ValidMenuKey>[] = React.useMemo(
    () => [
      {
        label: 'Name',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: CustomerUser) => row.fullName,
        filterable: true,
        dataProperty: 'fullName' as keyof CustomerUser,
      },
      {
        label: 'Username',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: CustomerUser) => row.name,
        filterable: true,
        component: ({ row }) => <UserDisplayName user={row} />,
        filterFunc: (row: CustomerUser) => row.name,
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Edit',
                key: 'Edit',
                icon: Edit,
              },
            ],
          },
          {
            options: [
              {
                label: 'Check Permissions',
                key: 'CheckPermissions',
                visible: canCheckPermissions,
              },
            ],
          },
          {
            options: [
              {
                label: 'Nebula Logs',
                key: 'NebulaLogs',
                visible: canViewLogs,
              },
            ],
          },
        ],
      },
    ],
    [canViewLogs, agencyUsersLookup],
  )

  return (
    <React.Fragment>
      {formFactor.isPhone && (
        <PageTitleSubtitle>
          <LicenseCount
            availableLicenses={availableLicenses}
            className={clsx(classes.licenseCount, classes.licenseCountPhone)}
            totalLicenses={agency ? agency.licenseCount ?? 0 : 0}
          />
        </PageTitleSubtitle>
      )}
      <PageTitleRightActions>
        {!formFactor.isPhone && (
          <LicenseCount
            availableLicenses={availableLicenses}
            className={classes.licenseCount}
            totalLicenses={agency ? agency.licenseCount ?? 0 : 0}
          />
        )}
        {!formFactor.isPhone && (
          <SplitButton
            color='primary'
            disabled={availableLicenses === 0}
            onClick={selectAddUserOption}
            options={addUserOptions}
            triggerOnMenuSelection={true}
          />
        )}
        {formFactor.isPhone && (
          <IconButton
            onClick={() => selectUserForEditing(defaultUser)}
            color="primary"
            disabled={availableLicenses === 0}
            className={classes.iconButton}
            size="large">
            <AddCircle />
          </IconButton>
        )}
      </PageTitleRightActions>
      <Table
        data={licensedUsers}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<CustomerUser, ValidMenuKey>[]}
        ariaLabel="Users for Licensed Agency"
        useDefaultRow
        handleMenuClick={
          handleMenuClick as MenuClick<IdentifiableModel, ValidMenuKey>
        }
        selectedId={editingUser?.id}
        isLoading={isLoading}
        uniqueIdentifier="UsersList"
      />
      {(isEditing || isAdding || isBulkAdding) && (
        <SidePanelContent
          anchor="right"
          title={isEditing ? `Edit User` : isBulkAdding ? 'Add Multiple Users' : 'Add User'}
          onClose={() => selectUserForEditing(null)}
        >
          {isEditing && (
            <EditUser
              key={(editingUser as CustomerUser).id}
              user={editingUser as CustomerUser}
              onClose={() => selectUserForEditing(null)}
              onSave={(user) => onSave(user as CustomerUser)}
              onDelete={onDelete}
              canViewPermissions={authUser?.isSupport ?? false}
              canViewCapabilities={authUser?.isSupport ?? false}
              canViewGlobalConfiguration={authUser?.isSupport ?? false}
            />
          )}
          {isAdding && (
            <AddUser
              key={(editingUser as CustomerUser).id}
              onClose={() => selectUserForEditing(null)}
              availableUsers={activeNonLicensedUsers}
              activeAdminUsers={activeAdminUsers}
              onAdd={(user) => onAdd(user as CustomerUser)}
              canViewPermissions={authUser?.isSupport ?? false}
              canViewCapabilities={authUser?.isSupport ?? false}
            />
          )}
          {isBulkAdding && (
            <AddMultipleUsers
              key={(editingUser as CustomerUser).id}
              onClose={() => selectUserForEditing(null)}
              availableLicenses={availableLicenses}
              availableUsers={activeNonLicensedUsers}
              activeAdminUsers={activeAdminUsers}
              onAdd={(users) => onAddMultipleUsers(users as CustomerUser[])}
          />
        )}
        </SidePanelContent>
      )}
      <Snackbar
        severity="success"
        open={addedUser !== null}
        onClose={() => setAddedUser(null)}
        actionButtonText="Add Device"
        onActionButtonClicked={() => navigateToDevices(addedUser!)}
      >
        {addedUser?.fullName} added successfully.
      </Snackbar>
      <Snackbar
        severity="success"
        open={addedUsers !== null}
        onClose={() => setAddedUsers(null)}
        actionButtonText="Add Devices"
        onActionButtonClicked={() => navigateToDevicesBulk(addedUsers!)}
      >
        {addedUsers?.length} user{addedUsers !== null && addedUsers.length != 1 ? 's' : ''} added successfully.
      </Snackbar>
      <ConfirmationModal
        onCancel={() => navigate(-1)}
        open={isPerformingPermissionCheck}
        title="Check Permissions For"
        hideActions
        className={classes.permissionChecking}
        message={
          <CheckPermissions
            key={permissionCheckMatch?.params.customerUserId as string}
            customerAgencyId={
              permissionCheckMatch?.params.customerAgencyId as string
            }
            customerUserId={
              permissionCheckMatch?.params.customerUserId as string
            }
          />
        }
      />
    </React.Fragment>
  );
}

export const UsersPage: PageDefinition = {
  routes: (user) => {
    if (!user?.customerAgencyId) {
      return [
        '/users/',
        '/users/:userId'
      ]
    }

    return [
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/users',
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/users/:userId',
      CheckPermissionPath,
    ]
  },
  type: [PageType.Agency],
  sortOrder: () => 0,
  component: <Users />,
  navLinkText: 'Users',
  pageTitle: buildPageTitle,
  canAccess: canViewAgencyPage,
  buildNavLink: (user) => {
    if (user === null) return ''

    if (!user.customerAgencyId || !user.licensedAgencyId) return '/users'

    return buildNavigationUrl(null, {
      customerAgencyId: user.customerAgencyId,
      licensedAgencyId: user.licensedAgencyId,
    })
  },
}
